'use client';

import { ChevronLeft, ChevronRight, PanelLeft } from 'lucide-react';
import { useLocale } from 'next-intl';
import {
  useEffect,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
} from 'react';
import { getDesktopWindowCopy } from '@/i18n/desktop-window-copy';
import { readDesktopTitlebarOverlay } from '@/lib/desktop-titlebar-layout';
import { DESKTOP_SIDEBAR_TOGGLE_EVENT } from '@/lib/desktop-window-appearance';
import {
  readDesktopHistoryState,
  subscribeDesktopHistory,
} from '@/lib/desktop-window-navigation';

const subscribeNothing = () => () => {};
function readOverlay() {
  return readDesktopTitlebarOverlay(
    'imaiDesktop' in window ? window.imaiDesktop : null
  );
}
function getNavigation(): unknown {
  return 'navigation' in window ? window.navigation : null;
}

/** Full-bleed desktop shells: the page owns the top row around native controls. */
export function DesktopWindowOverlay() {
  const supported = useSyncExternalStore(
    subscribeNothing,
    () => readOverlay() !== null,
    () => false
  );
  return supported ? <ActiveDesktopWindowOverlay /> : null;
}

function ActiveDesktopWindowOverlay() {
  const copy = getDesktopWindowCopy(useLocale());
  const [fullscreen, setFullscreen] = useState(false);
  const [history, setHistory] = useState(() =>
    readDesktopHistoryState(getNavigation())
  );

  useLayoutEffect(() => {
    const overlay = readOverlay();
    if (!overlay) return;
    const html = document.documentElement;
    html.setAttribute('data-desktop-overlay-titlebar', '');
    html.style.setProperty('--desktop-overlay-left', `${overlay.left}px`);
    html.style.setProperty('--desktop-overlay-right', `${overlay.right}px`);
    html.style.setProperty('--desktop-overlay-height', `${overlay.height}px`);
    const unsubscribe = overlay.subscribeFullscreen?.(setFullscreen);
    return () => {
      unsubscribe?.();
      html.removeAttribute('data-desktop-overlay-titlebar');
      html.removeAttribute('data-desktop-fullscreen');
      for (const name of ['left', 'right', 'height'])
        html.style.removeProperty(`--desktop-overlay-${name}`);
    };
  }, []);

  useEffect(() => {
    document.documentElement.toggleAttribute(
      'data-desktop-fullscreen',
      fullscreen
    );
  }, [fullscreen]);

  useEffect(() => {
    const navigation = getNavigation();
    const update = () => setHistory(readDesktopHistoryState(navigation));
    update();
    return subscribeDesktopHistory(navigation, update);
  }, []);

  return (
    <>
      <div data-desktop-window-drag aria-hidden="true" />
      <nav data-desktop-window-nav aria-label={copy.navigation}>
        <button
          type="button"
          className="imai-press"
          data-desktop-window-nav-toggle
          aria-label={copy.sidebar}
          title={copy.sidebar}
          onClick={() =>
            window.dispatchEvent(new Event(DESKTOP_SIDEBAR_TOGGLE_EVENT))
          }
        >
          <PanelLeft aria-hidden="true" />
        </button>
        <button
          type="button"
          className="imai-press"
          aria-label={copy.back}
          title={copy.back}
          disabled={!history.canGoBack}
          onClick={() => window.history.back()}
        >
          <ChevronLeft aria-hidden="true" />
        </button>
        <button
          type="button"
          className="imai-press"
          aria-label={copy.forward}
          title={copy.forward}
          disabled={!history.canGoForward}
          onClick={() => window.history.forward()}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      </nav>
    </>
  );
}
